/**
 * Branches service - handles API calls for branch management
 */

import { apiFetch } from "./apiClient";

const API_URL = import.meta.env.VITE_AUTH_API_URL;

export interface Branch {
  id: string;
  name: string;
  company_id: string;
  company_name: string;
  created_at: string;
  is_active: boolean;
  address?: string;
  backend_url?: string;
}

/**
 * Get all branches, optionally filtered by company
 */
export async function getBranches(companyId?: string): Promise<Branch[]> {
  const url = companyId
    ? `${API_URL}/branches?company_id=${companyId}`
    : `${API_URL}/branches`;

  const response = await apiFetch(url);

  if (!response.ok) {
    throw new Error('Failed to fetch branches');
  }

  const data = await response.json();
  return data.data || [];
}

/**
 * Get a branch by ID
 */
export async function getBranchById(id: string): Promise<Branch> {
  const response = await apiFetch(`${API_URL}/branches/${id}`);

  if (!response.ok) {
    throw new Error('Failed to fetch branch');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Create a new branch
 */
export async function createBranch(branchData: {
  name: string;
  company_id: string;
  address?: string;
  backend_url?: string;
}): Promise<Branch> {
  const response = await apiFetch(`${API_URL}/branches`, {
    method: 'POST',
    body: JSON.stringify(branchData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to create branch');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Update a branch.
 * Supports partial updates: name, is_active, address, backend_url.
 * At least one field must be provided.
 */
export async function updateBranch(
  id: string,
  branchData: { name?: string; is_active?: boolean; address?: string; backend_url?: string },
): Promise<Branch> {
  const response = await apiFetch(`${API_URL}/branches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(branchData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to update branch');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Delete a branch
 */
export async function deleteBranch(id: string): Promise<void> {
  const response = await apiFetch(`${API_URL}/branches/${id}`, { method: 'DELETE' });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Failed to delete branch');
  }
}
